import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useState } from "react";

import { notificationApi } from "@bw/shared/api";
import type {
  NotificationDeliveryRecoveryItem,
  NotificationDeliveryResolutionRequest,
} from "@bw/shared/types/generated";

type Resolution = NotificationDeliveryResolutionRequest["action"];
type PendingResolution = Readonly<{
  action: Resolution;
  item: NotificationDeliveryRecoveryItem;
}>;

const PAGE_SIZE = 20;

const ACTION_LABELS: Record<Resolution, string> = {
  retry_terminal: "Retry delivery",
  confirm_succeeded: "Confirm delivered",
  confirm_not_accepted: "Confirm not accepted",
  cancel_terminal: "Cancel delivery",
};

function availableActions(
  item: NotificationDeliveryRecoveryItem,
): readonly Resolution[] {
  if (item.status === "uncertain") {
    return ["confirm_succeeded", "confirm_not_accepted"];
  }
  return ["retry_terminal", "cancel_terminal"];
}

function formatTimestamp(value: string): string {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? "Time unavailable"
    : new Intl.DateTimeFormat(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(parsed);
}

export function DeliveryRecoveryPanel() {
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<PendingResolution | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const recoveryQuery = useInfiniteQuery({
    queryKey: ["notifications", "delivery-recovery"],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      notificationApi.listDeliveryRecovery({
        statuses: ["uncertain", "terminal_failure"],
        limit: PAGE_SIZE,
        ...(pageParam ? { cursor: pageParam } : {}),
      }),
    getNextPageParam: (lastPage) => {
      if (!lastPage.has_more) return undefined;
      if (!lastPage.next_cursor) {
        throw new Error("Recovery continuation is unavailable");
      }
      return lastPage.next_cursor;
    },
  });
  const resolution = useMutation({
    mutationFn: (request: PendingResolution) =>
      notificationApi.resolveDelivery(request.item.intent_id, {
        action: request.action,
      }),
    onSuccess: async (item, request) => {
      setPending(null);
      setFeedback(
        `${ACTION_LABELS[request.action]} recorded. Current state: ${item.status.replaceAll("_", " ")}.`,
      );
      await queryClient.invalidateQueries({
        queryKey: ["notifications", "delivery-recovery"],
      });
    },
  });

  const recoveryItems =
    recoveryQuery.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <Paper
      component="section"
      aria-labelledby="delivery-recovery-title"
      variant="outlined"
      sx={{ p: { xs: 2, md: 2.5 } }}
    >
      <Stack spacing={2}>
        <Box>
          <Typography
            id="delivery-recovery-title"
            component="h2"
            variant="h6"
            sx={{ fontWeight: 700 }}
          >
            Delivery recovery
          </Typography>
          <Typography color="text.secondary" variant="body2">
            Review provider outcomes that could not be confirmed automatically.
            Recipient identity and message content are intentionally hidden.
          </Typography>
        </Box>

        {feedback ? (
          <Alert severity="success" role="status">
            {feedback}
          </Alert>
        ) : null}
        {resolution.isError ? (
          <Alert severity="error" role="alert">
            The resolution was not recorded. Refresh the delivery state and try
            again.
          </Alert>
        ) : null}
        {recoveryQuery.isError && recoveryItems.length === 0 ? (
          <Alert
            severity="error"
            role="alert"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => recoveryQuery.refetch()}
              >
                Try again
              </Button>
            }
          >
            Delivery recovery is temporarily unavailable.
          </Alert>
        ) : null}
        {recoveryQuery.isFetchNextPageError ? (
          <Alert
            severity="error"
            role="alert"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => recoveryQuery.fetchNextPage()}
              >
                Retry
              </Button>
            }
          >
            More delivery outcomes could not be loaded. Earlier results are
            still available.
          </Alert>
        ) : null}

        {recoveryQuery.isPending ? (
          <Stack
            role="status"
            aria-label="Loading delivery recovery"
            spacing={1}
          >
            <Skeleton variant="rounded" height={72} />
            <Skeleton variant="rounded" height={72} />
          </Stack>
        ) : null}

        {recoveryQuery.isSuccess &&
        recoveryItems.length === 0 &&
        !recoveryQuery.hasNextPage ? (
          <Typography color="text.secondary" variant="body2">
            No delivery outcomes currently need review.
          </Typography>
        ) : null}

        {recoveryItems.map((item) => (
          <Paper key={item.intent_id} variant="outlined" sx={{ p: 1.5 }}>
            <Stack spacing={1.25}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                sx={{ alignItems: { xs: "flex-start", sm: "center" } }}
              >
                <Chip
                  size="small"
                  color={item.status === "uncertain" ? "warning" : "error"}
                  label={item.status.replaceAll("_", " ")}
                />
                <Chip size="small" variant="outlined" label={item.channel} />
                <Typography color="text.secondary" variant="caption">
                  {item.attempt_count} delivery{" "}
                  {item.attempt_count === 1 ? "attempt" : "attempts"}
                  {" · "}
                  Updated {formatTimestamp(item.updated_at)}
                </Typography>
              </Stack>
              {item.last_error_code ? (
                <Typography variant="body2">
                  Reason: {item.last_error_code.replaceAll("_", " ")}
                </Typography>
              ) : null}
              {item.provider_correlation_id ? (
                <Typography
                  color="text.secondary"
                  variant="caption"
                  sx={{ overflowWrap: "anywhere" }}
                >
                  Provider correlation: {item.provider_correlation_id}
                </Typography>
              ) : null}
              <Stack
                direction="row"
                spacing={1}
                useFlexGap
                sx={{ flexWrap: "wrap" }}
              >
                {availableActions(item).map((action) => (
                  <Button
                    key={action}
                    type="button"
                    size="small"
                    variant={
                      action === "cancel_terminal" ? "outlined" : "contained"
                    }
                    color={action === "cancel_terminal" ? "error" : "primary"}
                    disabled={resolution.isPending}
                    onClick={() => {
                      setFeedback(null);
                      resolution.reset();
                      setPending({ action, item });
                    }}
                  >
                    {ACTION_LABELS[action]}
                  </Button>
                ))}
              </Stack>
            </Stack>
          </Paper>
        ))}

        {recoveryQuery.hasNextPage ? (
          <Button
            type="button"
            variant="outlined"
            disabled={recoveryQuery.isFetchingNextPage || resolution.isPending}
            onClick={() => recoveryQuery.fetchNextPage()}
          >
            {recoveryQuery.isFetchingNextPage ? "Loading more…" : "Load more"}
          </Button>
        ) : recoveryItems.length > 0 ? (
          <Typography color="text.secondary" variant="body2">
            All recovery items are loaded.
          </Typography>
        ) : null}
      </Stack>

      <Dialog open={pending !== null} onClose={() => setPending(null)}>
        <DialogTitle>Confirm delivery resolution</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This records an operator decision for an unresolved provider
            outcome. Confirm only after checking the provider evidence.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button type="button" onClick={() => setPending(null)}>
            Go back
          </Button>
          <Button
            type="button"
            variant="contained"
            color={pending?.action === "cancel_terminal" ? "error" : "primary"}
            disabled={resolution.isPending || pending === null}
            onClick={() => pending && resolution.mutate(pending)}
          >
            {resolution.isPending
              ? "Recording…"
              : pending
                ? ACTION_LABELS[pending.action]
                : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
